/**
 * Helpers for reading per-field evidence metadata.
 *
 * BI-865 renamed call-extraction evidence from {source_call_id} to
 * {source_id, source_type: "call"}. AI-audit evidence uses
 * {source_id, source_type: "ai_audit"}. Pre-rename rows in
 * extracted_data.metadata.* (out of scope for the rename) and
 * unmigrated rows in output_data.metadata.* still carry the legacy
 * source_call_id. Readers normalize both shapes via the helpers here.
 *
 * Keep these helpers narrow and reusable so the eventual cleanup that
 * drops `source_call_id` happens in one place.
 */

/**
 * Source identifier shape carried on an evidence item or on field-level
 * metadata. All fields are optional; the helpers below cope with any
 * combination present (or absent).
 */
export interface EvidenceSource {
  source_id?: string;
  source_type?: 'call' | 'ai_audit';
  /** @deprecated Pre-BI-865 shape; readers fall back to this when source_id is absent. */
  source_call_id?: string;
}

/**
 * Returns the call_id to navigate to when "Review" is clicked on a
 * field whose source is a call. Matches either the BI-865 shape
 * (`source_type === 'call'` and `source_id`) or the legacy
 * `source_call_id`. Returns undefined for audit-sourced or unsourced
 * metadata — callers route those separately.
 */
export function getCallSourceId(meta?: EvidenceSource | null): string | undefined {
  if (!meta) return undefined;
  if (meta.source_type === 'call' && meta.source_id) {
    return meta.source_id;
  }
  return meta.source_call_id;
}

/**
 * Stamp the call being viewed onto every node of an output-metadata tree, so a
 * leaf's "Review" button can resolve which call to open.
 *
 * Arrays are MAPPED, never spread. Stamping with `{...value}` turns an entity
 * field's per-item metadata array (`fields.drug` is one entry per drug) into an
 * object with numeric keys; `NestedFieldAccordion` then fails its
 * `Array.isArray(outputMetaNode)` check, hands `undefined` to every array item,
 * and each nested leaf loses its evidence — no Review button and no chain of
 * thought — while the identical field kept both on the case-summary board, which
 * never stamps (LA-6912).
 *
 * Nested leaves need the stamp too, not just the top level: `getCallSourceId` is
 * called with the LEAF's metadata. Every object node is stamped on the way down;
 * container nodes gaining the two keys is harmless, because descent is driven by
 * schema field ids, never by iterating metadata keys.
 *
 * `evidence` is copied through untouched — its items carry their own source
 * fields and are not part of the field tree.
 */
export type StampedCallSource<T> = T extends readonly (infer U)[]
  ? StampedCallSource<U>[]
  : T extends object
    ? { [K in keyof T]: K extends 'evidence' ? T[K] : StampedCallSource<T[K]> } & EvidenceSource
    : T;

export function stampCallSource<T>(node: T, callId: string): StampedCallSource<T> {
  if (Array.isArray(node)) {
    return node.map(item => stampCallSource(item, callId)) as StampedCallSource<T>;
  }
  if (node === null || typeof node !== 'object') return node as StampedCallSource<T>;

  const out: Record<string, unknown> = { source_id: callId, source_type: 'call' };
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    out[key] = key === 'evidence' ? value : stampCallSource(value, callId);
  }
  return out as StampedCallSource<T>;
}
